import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { api } from './api';
import { readParentViewport } from './parentViewport';
import { hasAnalyticsConsent, isPortalSurface } from './analytics';

// Route categories only. Never send raw URLs, kid IDs, queries or page text.
export function parentPageTarget(path: string): string | null {
  if (!isPortalSurface(path)) return null;
  const p = path.split(/[?#]/)[0].replace(/\/$/, '');
  if (p === '/portal') return 'dashboard';
  if (p === '/portal/family/new' || p === '/portal/register') return 'family_setup';
  if (/^\/portal\/courses\/[^/]+$/.test(p)) return 'course_detail';
  if (/^\/portal\/checkout\/class\/[^/]+$/.test(p)) return 'class_checkout';
  if (p === '/portal/wallet/topup') return 'wallet_topup';
  const section = p.split('/')[2];
  const allowed: Record<string, string> = {
    explore: 'explore',
    more: 'more',
    family: 'family',
    courses: 'courses',
    classes: 'courses',
    learning: 'learning',
    projects: 'projects',
    study: 'study',
    guides: 'guides',
    academy: 'academy',
    tutoring: 'tutoring',
    teachers: 'tutoring',
    challenge: 'challenge',
    approvals: 'approvals',
    wallet: 'wallet',
    billing: 'billing',
    settings: 'settings',
    usage: 'usage',
    audit: 'activity',
    desktop: 'desktop',
    rewards: 'rewards',
  };
  return allowed[section] ?? null;
}
export function useParentPageTracking(enabled = true) {
  const { pathname, key } = useLocation();
  useEffect(() => {
    const target = parentPageTarget(pathname);
    if (!enabled || !target) return;
    let sent = false;
    let lastSnapshot = '';
    let lastSentAt = 0;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const allowed = () => hasAnalyticsConsent() && document.visibilityState !== 'hidden';
    const send = (view_kind: 'route' | 'viewport') => {
      if (!allowed()) return;
      const viewport = readParentViewport();
      const snapshot = JSON.stringify(viewport);
      if (sent && snapshot === lastSnapshot) return;
      lastSnapshot = snapshot;
      lastSentAt = Date.now();
      sent = true;
      void api('/analytics/parent-page', {
        method: 'POST',
        principal: 'user',
        body: {
          target,
          view_kind,
          viewport,
          analytics_consent: true,
          event_id: crypto.randomUUID(),
        },
      }).catch(() => undefined);
    };
    const cancel = () => {
      clearTimeout(timer);
      timer = undefined;
    };
    const sync = () => {
      cancel();
      if (!sent) send('route');
    };
    // Wait for scrolling to settle; at most one position checkpoint per five seconds.
    const schedule = () => {
      cancel();
      if (!allowed()) return;
      timer = setTimeout(
        () => send(sent ? 'viewport' : 'route'),
        Math.max(800, lastSentAt + 5000 - Date.now()),
      );
    };
    const onScroll = (event: Event) => {
      if (
        event.target === document ||
        event.target === document.querySelector('[data-testid="portal-scroll-region"]')
      )
        schedule();
    };
    sync();
    window.addEventListener('airbotix:analytics-consent', sync);
    document.addEventListener('visibilitychange', sync);
    document.addEventListener('scroll', onScroll, { passive: true, capture: true });
    window.addEventListener('resize', schedule);
    window.visualViewport?.addEventListener('scroll', schedule, { passive: true });
    window.visualViewport?.addEventListener('resize', schedule);
    return () => {
      cancel();
      window.removeEventListener('airbotix:analytics-consent', sync);
      document.removeEventListener('visibilitychange', sync);
      document.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', schedule);
      window.visualViewport?.removeEventListener('scroll', schedule);
      window.visualViewport?.removeEventListener('resize', schedule);
    };
  }, [pathname, key, enabled]);
}
